import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import KosyamLogo from "@/components/KosyamLogo";
import LanguageToggle from "@/components/LanguageToggle";
import useSubdomainSchool from "@/hooks/useSubdomainSchool";
import {
  GraduationCap, CurrencyDollar, ChartLineUp, ShieldCheck, DeviceMobile,
  Books, ClipboardText, ArrowRight, Sparkle, List, X,
} from "@phosphor-icons/react";

function PublicPlansGrid() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/subscription-plans`)
      .then(r => r.json()).then(d => setPlans(d.items || [])).catch(() => {});
  }, []);
  if (!plans.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-4" data-testid="plans-grid">
      <div className="text-[10px] uppercase tracking-widest text-secondary font-semibold mb-3">Nos offres</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map(p => (
          <div key={p.id} className="p-4 border border-border rounded-xl bg-card hover:border-primary/50 transition-colors" data-testid={`plan-${p.id}`}>
            <div className="font-heading text-lg font-bold">{p.name}</div>
            <div className="mt-1"><span className="font-heading text-2xl font-bold text-secondary tabular-nums">{p.price_monthly_xof.toLocaleString()}</span> <span className="text-xs text-muted-foreground">{p.currency}/mois</span></div>
            {p.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-3">{p.description}</div>}
            {p.max_students && <div className="text-[10px] text-muted-foreground mt-2">Jusqu'à <b>{p.max_students}</b> élèves · {p.max_campuses} campus</div>}
            <Link to="/souscription"><Button size="sm" variant="outline" className="w-full mt-3" data-testid={`plan-cta-${p.id}`}>Choisir cette offre</Button></Link>
          </div>
        ))}
      </div>
    </section>
  );
}


const PERSONA_IMAGES = [
  { img: "https://images.unsplash.com/photo-1548102245-c79dbcfa9f92?auto=format&fit=crop&w=700&q=75", offset: false, step: "01" },
  { img: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=700&q=75", offset: true,  step: "02" },
  { img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=700&q=80", offset: false, step: "03" },
  { img: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=700&q=80", offset: true,  step: "04" },
];

const FEATURE_ICONS = [GraduationCap, CurrencyDollar, Books, ClipboardText, ChartLineUp, ShieldCheck];
const FEATURE_KEYS = ["students", "fees", "grades", "attendance", "accounting", "security"];

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const { loading: subLoading, school: subdomainSchool } = useSubdomainSchool();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      if (user.role === "parent") nav("/portail", { replace: true });
      else if (user.role === "student") nav("/app/eleve", { replace: true });
      else nav("/app", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    if (!subLoading && subdomainSchool?.id && !user) {
      nav(`/ecoles/${subdomainSchool.id}`, { replace: true });
    }
  }, [subLoading, subdomainSchool, user, nav]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="link-home-header">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden text-primary shrink-0">
              <KosyamLogo size={40} />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-base sm:text-lg font-bold leading-none">Kosyam</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground mt-1 truncate">
                {t("app.brand.tagline_short")}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageToggle />
            <Link to="/souscription">
              <Button variant="outline" size="sm" data-testid="btn-demo-header">Demander une démo</Button>
            </Link>
            <Link to="/souscription">
              <Button size="sm" className="bg-primary text-primary-foreground" data-testid="btn-start-header">Commencer maintenant</Button>
            </Link>
            <Link to="/a-propos"><Button variant="ghost" size="sm" data-testid="btn-nav-about">{t("common.about")}</Button></Link>
            <Link to="/candidater"><Button variant="ghost" size="sm" data-testid="btn-nav-apply">{t("common.apply")}</Button></Link>
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="btn-nav-login">{t("auth.login")}</Button></Link>
          </div>

          {/* Mobile controls */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageToggle compact />
            <button
              type="button"
              onClick={() => setMobileOpen(v => !v)}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center"
              aria-label="Menu"
              data-testid="btn-mobile-menu"
            >
              {mobileOpen ? <X size={18} /> : <List size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu-panel">
            <div className="px-4 py-3 space-y-1 flex flex-col">
              <Link to="/a-propos" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="btn-mobile-about">{t("common.about")}</Button>
              </Link>
              <Link to="/candidater" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="btn-mobile-apply">{t("common.apply")}</Button>
              </Link>
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="btn-mobile-login">{t("auth.login")}</Button>
              </Link>
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-full mt-2" data-testid="btn-mobile-cta">
                  {t("common.get_started")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* SchoolExplorer bar */}
      <SchoolExplorer />

      {/* Public subscription plans grid */}
      <PublicPlansGrid />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-40" aria-hidden
             style={{backgroundImage:'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize:'4px 4px'}} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary text-[10px] sm:text-xs font-semibold tracking-wide uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
              {t("landing.kicker")}
            </div>
            <h1 className="mt-6 font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {t("landing.hero_line1")}<br />
              {t("landing.hero_line2")}<br />
              <span className="text-accent">{t("landing.hero_line3")}</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {t("landing.hero_desc_a")}
              {" "}<b className="text-foreground">{t("landing.hero_desc_b")}</b>{t("landing.hero_desc_c")}
            </p>
            <div className="mt-7 sm:mt-8 flex flex-wrap gap-3 items-center">
              <Link to="/souscription" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 rounded-full px-8" data-testid="btn-hero-start">
                  Commencer maintenant <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/souscription" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8" data-testid="btn-hero-demo">
                  Demander une démonstration
                </Button>
              </Link>
              <div className="w-full sm:w-auto mt-2 sm:mt-0 sm:ml-2 text-xs sm:text-sm">
                <span className="font-heading text-lg sm:text-xl font-bold text-secondary">À partir de 25 000 FCFA</span>
                <span className="text-muted-foreground"> / mois</span>
                <div className="text-[10px] text-muted-foreground">Tarification adaptée à la taille de votre établissement</div>
              </div>
            </div>
            <div className="mt-7 sm:mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 rounded-full px-8" data-testid="btn-hero-primary">
                  {t("landing.cta_open_school")}
                </Button>
              </Link>
              <a href="#fonctions" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8" data-testid="btn-hero-secondary">
                  {t("landing.cta_see_features")}
                </Button>
              </a>
            </div>
            <div className="mt-10 sm:mt-12 grid grid-cols-4 gap-4 sm:gap-6 max-w-lg">
              <Metric v="5" l={t("landing.metrics.institutions")} />
              <Metric v="15+" l={t("landing.metrics.roles")} />
              <Metric v="7+" l={t("landing.metrics.currencies")} />
              <Metric v="i18n" l={t("landing.metrics.i18n_label")} />
            </div>
          </div>

          <div className="lg:col-span-2 relative mt-4 lg:mt-0">
            <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-br from-secondary/15 via-accent/15 to-primary/15 blur-2xl" aria-hidden />
            <div className="relative grid grid-cols-2 gap-3">
              {PERSONA_IMAGES.map((p, i) => (
                <div
                  key={i}
                  className={`rounded-2xl overflow-hidden border border-border bg-card card-hover ${p.offset ? "mt-6 sm:mt-8" : ""}`}
                  data-testid={`persona-${p.step}`}
                >
                  <img src={p.img} alt={t(`landing.personas.${i+1}_alt`)} loading="lazy" className="w-full h-[160px] sm:h-[220px] object-cover" />
                  <div className="px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-widest text-secondary font-semibold">{p.step}</div>
                    <div className="text-sm font-heading font-bold mt-0.5">{t(`landing.personas.${i+1}_title`)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{t(`landing.personas.${i+1}_subtitle`)}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile Money badge — placed AFTER the images grid (no longer overlapping) */}
            <div className="mt-4 sm:mt-6 mx-auto max-w-[300px] bg-card rounded-xl border border-border px-4 py-3 flex items-center gap-3 shadow-sm">
              <DeviceMobile size={22} weight="duotone" className="text-accent" />
              <div className="text-xs">
                <div className="font-semibold">{t("landing.mobile_badge_title")}</div>
                <div className="text-[11px] text-muted-foreground">{t("landing.mobile_badge_desc")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="fonctions" className="border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-secondary">{t("landing.features_kicker")}</div>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              {t("landing.features_title_line1")}<br className="hidden sm:block" />
              {t("landing.features_title_line2")}
            </h2>
          </div>
          <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURE_KEYS.map((key, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={key} className="card-hover bg-card rounded-lg border border-border p-5 sm:p-6" data-testid={`feature-${i}`}>
                  <div className="w-11 h-11 rounded-md bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div className="font-heading font-semibold text-lg">{t(`landing.features.${key}_title`)}</div>
                  <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{t(`landing.features.${key}_desc`)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wide">
            <Sparkle size={12} weight="fill" /> {t("landing.cta_ribbon")}
          </div>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            {t("landing.cta_ready_title")}
          </h2>
          <p className="mt-4 text-muted-foreground">
            {t("landing.cta_ready_desc")}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/login">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 sm:px-10" data-testid="btn-cta-bottom">
                {t("landing.cta_login")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/candidater">
              <Button size="lg" variant="outline" className="rounded-full px-8 sm:px-10" data-testid="btn-cta-parents">
                {t("landing.cta_parents_apply")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 text-xs text-muted-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>{t("landing.footer_copyright")}</div>
          <div className="text-[10px] text-muted-foreground/80">
            {t("landing.footer_product_of")}{" "}
            <Link to="/a-propos" className="font-semibold text-foreground/80 hover:text-primary transition-colors" data-testid="link-about-footer">Nathor Technologies</Link>
            {" · "}
            <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" data-testid="link-nathor-footer-external">nathortechnologies.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Metric({ v, l }) {
  return (
    <div>
      <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-primary">{v}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 leading-tight">{l}</div>
    </div>
  );
}

function SchoolExplorer() {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/schools`)
      .then(r => r.json()).then(setSchools).catch(() => {});
  }, []);
  if (!schools.length) return null;
  return (
    <section className="bg-muted/30 border-b border-border" data-testid="school-explorer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 overflow-x-auto">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 font-semibold">
          {t("landing.schools_bar")}
        </div>
        {schools.map(s => (
          <Link key={s.id} to={`/ecoles/${s.id}`}
            className="shrink-0 text-xs bg-card border border-border rounded-full px-3 py-1.5 hover:border-primary transition-colors flex items-center gap-2"
            data-testid={`ecole-chip-${s.id}`}
            style={s.primary_color ? { borderColor: `${s.primary_color}40` } : {}}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: s.primary_color || "#1a2a6c" }} />
            {s.name}
          </Link>
        ))}
      </div>
    </section>
  );
}
